import {
  BanknoteIcon,
  BellIcon,
  BookMarkedIcon,
  CalendarRangeIcon,
  ChartColumnIcon,
  ClipboardCheckIcon,
  CreditCardIcon,
  FileClockIcon,
  FolderTreeIcon,
  GaugeIcon,
  GavelIcon,
  GraduationCapIcon,
  HistoryIcon,
  LayersIcon,
  LayoutDashboardIcon,
  LibraryIcon,
  type LucideIcon,
  MailIcon,
  MegaphoneIcon,
  MonitorPlayIcon,
  NewspaperIcon,
  PenLineIcon,
  RadioIcon,
  ReceiptTextIcon,
  RotateCcwIcon,
  ScaleIcon,
  ShieldAlertIcon,
  ShieldCheckIcon,
  SlidersHorizontalIcon,
  SparklesIcon,
  StarIcon,
  TriangleAlertIcon,
  UserXIcon,
  UsersIcon,
  UsersRoundIcon,
  WalletIcon,
} from "lucide-react";

import { PAGE_PATHS } from "@/constants";
import type { UserRole } from "@/types";

import { matchesPath } from "./app-sidebar/nav-tree";

export interface NavChild {
  to: string;
  labelKey: string;
  /**
   * Yig'ilgan paneldagi menyuda va ochiq paneldagi ro'yxatda yorliq oldida
   * turadi. Ixtiyoriy — ikonkasiz band ham to'g'ri chiziladi.
   */
  icon?: LucideIcon;
  /**
   * Bo'lsa, bu band ham havola emas — ochiluvchi guruh: yig'ilgan panelda
   * yon tomonga ochiladigan ikkinchi menyu, ochiq panelda ichma-ich ro'yxat.
   */
  children?: NavChild[];
}

export interface NavItem {
  to: string;
  labelKey: string;
  icon: LucideIcon;
  /** `NavLink end` — faqat aynan shu manzilda faol bo'lsin. */
  end?: boolean;
  /**
   * Shu bandni ko'ra oladigan rollar. Berilmasa — panelga kira oladigan
   * hamma ko'radi.
   */
  roles?: readonly UserRole[];
  /** Bola bandlar. Bo'lsa, ota band havola emas — ochiluvchi bo'lim. */
  children?: NavChild[];
}

export interface NavSection {
  labelKey: string;
  items: NavItem[];
}

/**
 * Menyu bo'limlari.
 *
 * `roles` — QULAYLIK, xavfsizlik emas. Menyudan yashirilgan sahifaga manzil
 * orqali baribir kirish mumkin; uni backend'dagi `@Roles` / `@AdminOnly`
 * qorovuli to'xtatadi. Bu yerdagi ro'yxat faqat "borib bo'lmaydigan eshikni
 * ko'rsatmaslik" uchun.
 *
 * Rol chegaralari backend kontrollerlaridan olingan:
 *  - `announcements.controller.ts` butunlay `@Roles("admin")` — moderator
 *    e'lon yubora olmaydi, shuning uchun band unga ko'rinmaydi.
 *  - Qolgan bo'limlar `@ModeratorUp()` yoki `@TeacherUp()` ostida, ya'ni
 *    moderator ular ichiga kira oladi (ba'zi AMALLAR admin uchun qolsa ham —
 *    masalan to'lovni qaytarish; ular ekran ichida yopiladi).
 */
export const NAV_SECTIONS: NavSection[] = [
  {
    labelKey: "NAV.SECTION_GENERAL",
    items: [
      {
        to: PAGE_PATHS.DASHBOARD,
        labelKey: "NAV.DASHBOARD",
        icon: LayoutDashboardIcon,
        end: true,
      },
    ],
  },
  {
    labelKey: "NAV.SECTION_MODERATION",
    items: [
      {
        to: PAGE_PATHS.COURSE_MODERATION,
        labelKey: "NAV.COURSE_MODERATION",
        icon: ShieldCheckIcon,
      },
      { to: PAGE_PATHS.POST_MODERATION, labelKey: "NAV.POST_MODERATION", icon: PenLineIcon },
      { to: PAGE_PATHS.REVIEW_MODERATION, labelKey: "NAV.REVIEW_MODERATION", icon: StarIcon },
      // `/ai/moderation/flags` — `@ModeratorUp()`: AI filtri belgilagan kontent.
      { to: PAGE_PATHS.AI_FLAGS, labelKey: "NAV.AI_FLAGS", icon: ShieldAlertIcon },
      {
        to: PAGE_PATHS.TEACHER_REQUESTS,
        labelKey: "NAV.TEACHER_REQUESTS",
        icon: ClipboardCheckIcon,
      },
      // `GET /enrollments/removals` — `@ModeratorUp()`: moderator ham ko'radi,
      // qaytarish tugmasi esa sahifa ichida faqat adminga chiqadi.
      { to: PAGE_PATHS.REMOVALS, labelKey: "NAV.REMOVALS", icon: UserXIcon },
      // `/reports/moderators/me*` — `@ModeratorUp()`: har kim FAQAT o'z
      // qarorlarini ko'radi (admin ham moderatsiya qiladi).
      { to: PAGE_PATHS.MY_DECISIONS, labelKey: "NAV.MY_DECISIONS", icon: GavelIcon },
    ],
  },
  {
    labelKey: "NAV.SECTION_CONTENT",
    items: [
      { to: PAGE_PATHS.COURSES, labelKey: "NAV.COURSES", icon: MonitorPlayIcon },
      { to: PAGE_PATHS.LIVE_COURSES, labelKey: "NAV.LIVE_COURSES", icon: RadioIcon },
      { to: PAGE_PATHS.NEWS, labelKey: "NAV.NEWS", icon: NewspaperIcon },
    ],
  },
  {
    labelKey: "NAV.SECTION_PEOPLE",
    items: [
      { to: PAGE_PATHS.USERS, labelKey: "NAV.USERS", icon: UsersIcon },
      { to: PAGE_PATHS.CONTACT_MESSAGES, labelKey: "NAV.CONTACT_MESSAGES", icon: MailIcon },
    ],
  },
  {
    labelKey: "NAV.SECTION_MONEY",
    items: [
      { to: PAGE_PATHS.PAYMENTS, labelKey: "NAV.PAYMENTS", icon: CreditCardIcon, end: true },
      // Kartaga to'lov: cheklarni tasdiqlash, o'qituvchiga pul o'tkazish va
      // pulni qaytarish — hammasi `@AdminOnly()`.
      {
        to: PAGE_PATHS.CARD_REVIEW,
        labelKey: "NAV.CARD_REVIEW",
        icon: ReceiptTextIcon,
        roles: ["admin"],
      },
      {
        to: PAGE_PATHS.WITHDRAWALS,
        labelKey: "NAV.WITHDRAWALS",
        icon: BanknoteIcon,
        roles: ["admin"],
      },
      {
        to: PAGE_PATHS.REFUND_REQUESTS,
        labelKey: "NAV.REFUND_REQUESTS",
        icon: RotateCcwIcon,
        roles: ["admin"],
      },
      { to: PAGE_PATHS.ANALYTICS, labelKey: "NAV.ANALYTICS", icon: ChartColumnIcon },
    ],
  },
  {
    // `reports.controller.ts` va `activity.controller.ts` — `@AdminOnly()`.
    // Moderatorga butun bo'lim ko'rinmaydi (bo'sh bo'lim o'zi tushib qoladi).
    labelKey: "NAV.SECTION_REPORTS",
    items: [
      {
        to: PAGE_PATHS.REPORT_TEACHERS,
        labelKey: "NAV.REPORT_TEACHERS",
        icon: GraduationCapIcon,
        roles: ["admin"],
      },
      {
        to: PAGE_PATHS.REPORT_STUDENTS,
        labelKey: "NAV.REPORT_STUDENTS",
        icon: UsersRoundIcon,
        roles: ["admin"],
      },
      {
        to: PAGE_PATHS.REPORT_COURSES,
        labelKey: "NAV.REPORT_COURSES",
        icon: LibraryIcon,
        roles: ["admin"],
      },
      {
        to: PAGE_PATHS.REPORT_AT_RISK,
        labelKey: "NAV.REPORT_AT_RISK",
        icon: TriangleAlertIcon,
        roles: ["admin"],
      },
      {
        to: PAGE_PATHS.REPORT_COHORTS,
        labelKey: "NAV.REPORT_COHORTS",
        icon: CalendarRangeIcon,
        roles: ["admin"],
      },
      {
        to: PAGE_PATHS.REPORT_PAYOUTS,
        labelKey: "NAV.REPORT_PAYOUTS",
        icon: WalletIcon,
        roles: ["admin"],
      },
      {
        // `grade-changes.controller.ts` moderatorga ham ochiq, lekin bo'lim
        // hisobotlar ichida — hozircha faqat admin.
        to: PAGE_PATHS.REPORT_GRADE_CHANGES,
        labelKey: "NAV.REPORT_GRADE_CHANGES",
        icon: FileClockIcon,
        roles: ["admin"],
      },
      {
        to: PAGE_PATHS.REPORT_MODERATORS,
        labelKey: "NAV.REPORT_MODERATORS",
        icon: ScaleIcon,
        roles: ["admin"],
      },
      {
        to: PAGE_PATHS.REPORT_ACTIVITY,
        labelKey: "NAV.REPORT_ACTIVITY",
        icon: HistoryIcon,
        roles: ["admin"],
      },
    ],
  },
  {
    labelKey: "NAV.SECTION_SYSTEM",
    items: [
      {
        // Ikkita ro'yxat ikkita sahifa, lekin menyuning birinchi qavatida
        // ikkita band emas: ular bitta narsaning bo'laklari va yopilgan
        // holda bitta qator egallaydi (qahvadosh panelidagi "Ma'lumotnoma"
        // bo'limi bilan bir xil naqsh).
        //
        // Bolalarning ikonkasi o'z sahifasidagi bilan bir xil (bo'sh
        // holatdagi `FolderTreeIcon` va `LayersIcon`). Ota band esa
        // o'zinikini oladi: daraxt ikonkasi unda ham tursa, ochiq panelda u
        // ketma-ket ikki qatorda takrorlanardi.
        to: PAGE_PATHS.CATEGORIES,
        labelKey: "NAV.REFERENCE",
        icon: BookMarkedIcon,
        children: [
          { to: PAGE_PATHS.CATEGORIES, labelKey: "NAV.CATEGORIES", icon: FolderTreeIcon },
          { to: PAGE_PATHS.COURSE_LEVELS, labelKey: "NAV.COURSE_LEVELS", icon: LayersIcon },
        ],
      },
      {
        to: PAGE_PATHS.ANNOUNCEMENTS,
        labelKey: "NAV.ANNOUNCEMENTS",
        icon: MegaphoneIcon,
        // `announcements.controller.ts`: `@Roles("admin")` — moderatorga yopiq.
        roles: ["admin"],
      },
      { to: PAGE_PATHS.NOTIFICATIONS, labelKey: "NAV.NOTIFICATIONS", icon: BellIcon },
      {
        // O'qish moderatorga ham ochiq, lekin o'zgartirish faqat admin —
        // moderator uchun bu yerda qiladigan ish yo'q.
        to: PAGE_PATHS.PLATFORM_SETTINGS,
        labelKey: "NAV.PLATFORM_SETTINGS",
        icon: SlidersHorizontalIcon,
        roles: ["admin"],
      },
      {
        // AI modellari, kalitlari va byudjeti — o'zgartirish faqat admin
        // (`/ai/admin/*` da `@AdminOnly()`), moderator uchun ish yo'q.
        to: PAGE_PATHS.AI,
        labelKey: "NAV.AI",
        icon: SparklesIcon,
        roles: ["admin"],
      },
      { to: PAGE_PATHS.HEALTH, labelKey: "NAV.HEALTH", icon: GaugeIcon },
    ],
  },
];

/** Bir bandni shu roldagi odam ko'ra oladimi. */
export const canSeeNavItem = (item: NavItem, role: UserRole | undefined): boolean =>
  !item.roles || (!!role && item.roles.includes(role));

/** Bo'sh qolgan bo'limlar butunlay tushib qoladi. */
export const visibleSections = (role: UserRole | undefined): NavSection[] =>
  NAV_SECTIONS.map(section => ({
    ...section,
    items: section.items.filter(item => canSeeNavItem(item, role)),
  })).filter(section => section.items.length > 0);

interface NavMatch {
  labelKey: string;
  sectionKey: string;
  path: string;
  depth: number;
}

/**
 * Manzilga ENG ANIQ mos band — menyuning istalgan qavatida.
 *
 * Avval eng uzun mos manzil: `/a/deep` sahifasida uning qo'shnisi `/a` ham
 * mos keladi, lekin breadcrumb'da aniqrog'i turishi kerak. Manzil teng
 * bo'lsa — chuqurrog'i: ota band bolasi bilan bir xil manzilga ega bo'lishi
 * mumkin (`NAV.REFERENCE` → `/categories`), breadcrumb'da esa guruh nomi
 * emas, sahifa nomi kerak. U ham teng bo'lsa — ro'yxatda birinchisi.
 *
 * Sarlavha ham, bo'lim ham shu bitta qidiruvdan olinadi — ular bir-biriga
 * zid chiqa olmaydi.
 */
const findBestMatch = (pathname: string, sections: readonly NavSection[]): NavMatch | undefined => {
  let best: NavMatch | undefined;

  const isBetter = (path: string, depth: number) =>
    !best ||
    path.length > best.path.length ||
    (path.length === best.path.length && depth > best.depth);

  const visit = (items: readonly NavChild[], sectionKey: string, depth: number) => {
    for (const item of items) {
      if (matchesPath(pathname, item.to) && isBetter(item.to, depth)) {
        best = { labelKey: item.labelKey, sectionKey, path: item.to, depth };
      }
      if (item.children) visit(item.children, sectionKey, depth + 1);
    }
  };

  for (const section of sections) visit(section.items, section.labelKey, 0);
  return best;
};

/** Breadcrumb uchun: manzilga mos band sarlavhasining kaliti. */
export const findNavLabelKey = (
  pathname: string,
  sections: readonly NavSection[] = NAV_SECTIONS,
): string | undefined => findBestMatch(pathname, sections)?.labelKey;

/** Breadcrumb uchun: manzil qaysi bo'limga tegishli. */
export const findNavSectionKey = (
  pathname: string,
  sections: readonly NavSection[] = NAV_SECTIONS,
): string | undefined => findBestMatch(pathname, sections)?.sectionKey;
